import InfoIcon from '@mui/icons-material/Info';
import { Tooltip } from '@mui/material';
import React from 'react';
import { COMPLIANCE_COLOR } from '../pages/store-details/components/PlanogramComplianceControlCenter';
import { BBox, BoxAction, CameraPog, PogBBox } from '../types';
import { ImageBox } from './BoxedImage';
import { ImageViewer } from './ImageViewer';

interface CameraImageViewerProps {
  // Camera image props
  preloadedImage?: string;
  fallbackPogBoxColor?: string;
  selectedPogBoxColor?: string;
  highlightedIlItemId?: number;
  filteredBboxes: BBox[];
  boxActions: BoxAction[];
  productRecognitionDetections: ImageBox[];
  planogramComplianceActions: ImageBox[];
  showProductImages: boolean;
  showFallbackImages?: boolean;
  onBoxClick: (bbox: BBox) => void;
  onProductRecognitionClick?: (detection: ImageBox) => void;
  onPlanogramComplianceClick?: (detection: ImageBox) => void;
  titleActions?: React.ReactNode;
  onBoxesSelected?: (selectedBboxes: BBox[]) => void;
  onBoxRightClick?: (detection: PogBBox | ImageBox) => void;

  // POG images props
  showPogs: boolean;
  currentPogs: CameraPog[];
  preloadedImagesB64: Record<string, string>;
  selectedIlItemIds: Set<number>;
  planogramStatusMap: Map<number, string>;
  onPogBoxClick: (bbox: PogBBox) => void;
  onPogBoxRightClick?: (bbox: PogBBox) => void;
  showShelfColumnLabels?: boolean;
  showEmbeddingsCount?: boolean;
  pogHeader?: React.ReactNode;
  pogHeaderActions?: React.ReactNode;
}

export const CameraImageViewer: React.FC<CameraImageViewerProps> = (props) => {
  const {
    // Camera image props
    preloadedImage,
    fallbackPogBoxColor,
    filteredBboxes,
    highlightedIlItemId,
    boxActions,
    productRecognitionDetections,
    planogramComplianceActions,
    showProductImages,
    showFallbackImages,
    onBoxClick,
    onProductRecognitionClick,
    onPlanogramComplianceClick,
    titleActions,
    onBoxesSelected,
    onBoxRightClick,

    // POG images props
    showPogs,
    currentPogs,
    preloadedImagesB64,
    selectedIlItemIds,
    planogramStatusMap,
    onPogBoxClick,
    onPogBoxRightClick,
    showShelfColumnLabels = false,
    showEmbeddingsCount = false,
    pogHeader,
    pogHeaderActions,
  } = props;
  const selectedPogBoxColor = props.selectedPogBoxColor || '#2563eb';
  return (
    <div
      className={`grid grid-cols-1 ${
        showPogs && currentPogs.length > 0 ? 'lg:grid-cols-2' : ''
      } gap-4`}
    >
      {/* Camera Image */}
      <div className="space-y-2">
        <div className="flex items-center justify-between min-h-10">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">Camera Image</h3>
            <Tooltip title="Hold Shift and drag to select multiple boxes" arrow>
              <InfoIcon className="text-gray-500 cursor-help" fontSize="small" />
            </Tooltip>
          </div>
          {titleActions}
        </div>
        <ImageViewer<BBox>
          imageBase64={preloadedImage?.startsWith('data:') ? preloadedImage : undefined}
          bboxes={filteredBboxes}
          onBoxClick={onBoxClick}
          onBoxRightClick={onBoxRightClick}
          boxActions={boxActions}
          productRecognitionDetections={productRecognitionDetections}
          onProductRecognitionClick={onProductRecognitionClick}
          planogramComplianceActions={planogramComplianceActions}
          onPlanogramComplianceClick={onPlanogramComplianceClick}
          enableZoom={true}
          showProductImages={showProductImages}
          showFallbackImages={showFallbackImages}
          onBoxesSelected={onBoxesSelected}
          showShelfColumnLabels={showShelfColumnLabels}
          showEmbeddingsCount={showEmbeddingsCount}
        />
      </div>

      {/* POG Images - only show if there are POGs and showPogs is enabled */}
      {showPogs && currentPogs.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between min-h-10">
            <h3 className="text-lg font-semibold">
              POG Images ({currentPogs.length})
              {pogHeader && (
                <span className="ml-2 text-sm font-normal text-gray-800">{pogHeader}</span>
              )}
            </h3>
            {pogHeaderActions}
          </div>
          <div className={`grid grid-cols-1 lg:grid-cols-${currentPogs.length || 1} gap-4`}>
            {currentPogs.map((pog, pogIndex) => {
              const preloadedPogImage = pog?.pogImageUri
                ? preloadedImagesB64[pog.pogImageUri]
                : undefined;

              // Map each pog bbox to include color and label based on compliance status
              const pogBboxesWithColor = (pog?.bboxes ?? []).map((bbox) => {
                let color = bbox.modelMatched ? '#2196F3' : (fallbackPogBoxColor ?? '#3b82f6'); // blue for model-matched, fallback otherwise

                // Check if this POG item should be highlighted
                // Since both planogram and realogram clicks now use comprehensive selection,
                // we just need to check if this item is in the selected set
                let isSelected = false;
                if (selectedIlItemIds.size > 0) {
                  isSelected = selectedIlItemIds.has(bbox.ilItemId);
                }

                // If highlighted, use blue color. Otherwise, use compliance status color
                if (highlightedIlItemId && bbox.ilItemId === highlightedIlItemId) {
                  color = '#FFFF00';
                } else if (isSelected) {
                  color = selectedPogBoxColor;
                } else if (bbox.ilItemId && planogramStatusMap.has(bbox.ilItemId)) {
                  const status = planogramStatusMap.get(bbox.ilItemId);
                  if (status && status in COMPLIANCE_COLOR) {
                    color = COMPLIANCE_COLOR[status as keyof typeof COMPLIANCE_COLOR];
                  }
                }

                // Build shelf-column label with mod index
                let shelfColumnLabel: string | undefined;
                if (bbox.shelf !== undefined && bbox.column !== undefined) {
                  // Only include mod number if there are multiple POGs
                  shelfColumnLabel =
                    currentPogs.length > 1
                      ? `${pogIndex + 1}-${bbox.shelf}-${bbox.column}`
                      : `${bbox.shelf}-${bbox.column}`;
                }

                return {
                  ...bbox,
                  color,
                  shelfColumnLabel,
                  embeddingCropCount: bbox.embeddingCropCount,
                };
              });

              return (
                <div key={`pog-${pog?.ilSectionId}`}>
                  {pog?.pogImageUri ? (
                    <ImageViewer<PogBBox>
                      imageBase64={
                        preloadedPogImage?.startsWith('data:') ? preloadedPogImage : undefined
                      }
                      bboxes={pogBboxesWithColor}
                      onBoxClick={onPogBoxClick}
                      onBoxRightClick={
                        onPogBoxRightClick ? (box) => onPogBoxRightClick(box as PogBBox) : undefined
                      }
                      boxActions={[]}
                      showShelfColumnLabels={showShelfColumnLabels}
                      showEmbeddingsCount={showEmbeddingsCount}
                      showFallbackImages={showFallbackImages}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-48 bg-gray-100 rounded">
                      <p className="text-gray-500 text-sm">No POG image available</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
